/**
 * NursingVisitNote — Standalone nursing visit note
 * Used for walk-in injections (B12, testosterone), wound checks, etc.
 * Lives in the patient chart timeline alongside SOAP notes.
 * Can be initiated from the nurse's workflow or patient chart.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { toast } from 'sonner';
import {
  Stethoscope, Check, Loader2, X, Plus, Syringe, FlaskConical,
  ChevronLeft, User, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ProcedureDocumentationModal from '../components/ProcedureDocumentationModal';

const VITAL_FIELDS = [
  { key: 'bp', label: 'BP', placeholder: '120/80' },
  { key: 'hr', label: 'HR', placeholder: '72' },
  { key: 'temp', label: 'Temp', placeholder: '98.6°F' },
  { key: 'rr', label: 'RR', placeholder: '16' },
  { key: 'o2_sat', label: 'O₂ Sat', placeholder: '98%' },
  { key: 'weight', label: 'Weight', placeholder: 'lbs' },
  { key: 'height', label: 'Height', placeholder: 'in' },
];

export default function NursingVisitNote() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [showProcModal, setShowProcModal] = useState(false);
  const [executions, setExecutions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [form, setForm] = useState({
    visit_date: new Date().toISOString().slice(0, 10),
    visit_time: new Date().toTimeString().slice(0, 5),
    chief_complaint: '',
    vitals: {},
    subjective: '',
    objective: '',
    assessment: '',
    requires_cosign: true,
  });

  const [visitId, setVisitId] = useState(null);  // set after first save

  useEffect(() => {
    if (patientId) {
      api.get(`/patients/${patientId}`).then(r => {
        const p = r.data?.patient || r.data;
        setPatient(p);
      }).catch(() => {});
    }
  }, [patientId]);

  const setVital = (key, val) => setForm(prev => ({
    ...prev, vitals: { ...prev.vitals, [key]: val }
  }));

  const set = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  const saveVisit = async (andClose = false) => {
    if (!form.chief_complaint) { toast.error('Chief complaint is required'); return; }
    setSaving(true);
    try {
      if (visitId) {
        await api.put(`/patients/${patientId}/nursing-visits/${visitId}`, form);
      } else {
        const res = await api.post(`/patients/${patientId}/nursing-visits`, form);
        setVisitId(res.data.visit_id);
      }
      setSaved(true);
      if (andClose) {
        toast.success('Nursing visit saved');
        navigate(`/patients/${patientId}`);
      }
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const patientName = patient ? `${patient.name?.first || ''} ${patient.name?.last || ''}`.trim() : 'Patient';

  return (
    <div className="h-full flex flex-col bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <Stethoscope className="w-4 h-4 text-teal-600" />
              Nursing Visit Note
            </h1>
            {patient && (
              <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                <User className="w-3 h-3" /> {patientName}
                {patient.dob && ` · DOB: ${patient.dob}`}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="text-xs h-8 gap-1"
            onClick={() => saveVisit(false)} disabled={saving}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Save Draft
          </Button>
          <Button size="sm" className="text-xs h-8 gap-1 bg-teal-600 hover:bg-teal-700"
            onClick={() => saveVisit(true)} disabled={saving}
            data-testid="save-nursing-visit">
            <Check className="w-3.5 h-3.5" /> Save &amp; File to Chart
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto px-6 py-5 space-y-5">

        {/* Date/Time + Co-sign */}
        <div className="flex items-center gap-4 bg-white rounded-xl border border-slate-200 px-4 py-3">
          <div>
            <label className="text-[9px] text-slate-400 uppercase font-semibold">Visit Date</label>
            <input type="date" value={form.visit_date} onChange={e => set('visit_date', e.target.value)}
              className="block mt-0.5 px-2 py-1 text-xs border border-slate-200 rounded-lg focus:outline-none" />
          </div>
          <div>
            <label className="text-[9px] text-slate-400 uppercase font-semibold">Visit Time</label>
            <input type="time" value={form.visit_time} onChange={e => set('visit_time', e.target.value)}
              className="block mt-0.5 px-2 py-1 text-xs border border-slate-200 rounded-lg focus:outline-none" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <label className="text-[11px] text-slate-600 font-medium flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={form.requires_cosign}
                onChange={e => set('requires_cosign', e.target.checked)}
                className="accent-teal-600 w-3.5 h-3.5" />
              Requires physician co-signature
            </label>
          </div>
        </div>

        {/* Chief Complaint */}
        <div className="bg-white rounded-xl border border-slate-200 px-4 py-3">
          <label className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Chief Complaint / Reason for Visit *</label>
          <input value={form.chief_complaint} onChange={e => set('chief_complaint', e.target.value)}
            placeholder="e.g., B12 injection per standing order, walk-in for Rocephin injection"
            className="w-full mt-1 px-2.5 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-200" />
        </div>

        {/* Vitals */}
        <div className="bg-white rounded-xl border border-slate-200 px-4 py-3">
          <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mb-2">Vitals</p>
          <div className="grid grid-cols-4 gap-3">
            {VITAL_FIELDS.map(v => (
              <div key={v.key}>
                <label className="text-[9px] text-slate-400 uppercase font-semibold">{v.label}</label>
                <input value={form.vitals[v.key] || ''} onChange={e => setVital(v.key, e.target.value)}
                  placeholder={v.placeholder}
                  className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none" />
              </div>
            ))}
          </div>
        </div>

        {/* Procedures performed */}
        <div className="bg-white rounded-xl border border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Procedures Performed</p>
            {visitId && (
              <button onClick={() => setShowProcModal(true)}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-700 text-[11px] font-medium">
                <Plus className="w-3 h-3" /> Add Procedure
              </button>
            )}
            {!visitId && (
              <p className="text-[10px] text-amber-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Save draft first to add procedures
              </p>
            )}
          </div>
          {executions.length === 0 ? (
            <p className="text-[11px] text-slate-400 py-2">No procedures documented yet</p>
          ) : (
            <div className="space-y-1">
              {executions.map(e => (
                <div key={e.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-50 border border-purple-100">
                  <Syringe className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                  <p className="text-xs font-medium text-purple-800">{e.procedure_name}</p>
                  <span className="text-[10px] text-purple-500 ml-auto">{e.billing_code}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Narrative note */}
        <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 space-y-3">
          <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Nursing Notes</p>
          <div>
            <label className="text-[9px] text-slate-400 uppercase font-semibold">Subjective / Patient Statement</label>
            <textarea value={form.subjective} onChange={e => set('subjective', e.target.value)}
              rows={2} placeholder="Patient reports..."
              className="w-full mt-0.5 px-2.5 py-2 text-xs border border-slate-200 rounded-lg resize-none focus:outline-none" />
          </div>
          <div>
            <label className="text-[9px] text-slate-400 uppercase font-semibold">Objective / Observations</label>
            <textarea value={form.objective} onChange={e => set('objective', e.target.value)}
              rows={2} placeholder="Patient tolerated procedure well. No adverse reactions..."
              className="w-full mt-0.5 px-2.5 py-2 text-xs border border-slate-200 rounded-lg resize-none focus:outline-none" />
          </div>
          <div>
            <label className="text-[9px] text-slate-400 uppercase font-semibold">Assessment / Disposition</label>
            <textarea value={form.assessment} onChange={e => set('assessment', e.target.value)}
              rows={2} placeholder="Patient discharged in stable condition. Follow-up as ordered..."
              className="w-full mt-0.5 px-2.5 py-2 text-xs border border-slate-200 rounded-lg resize-none focus:outline-none" />
          </div>
        </div>

      </div>

      {/* Procedure documentation modal */}
      {showProcModal && visitId && (
        <ProcedureDocumentationModal
          encounterId={visitId}  // nursing visits use visit_id as the reference
          task={{ patient_name: patientName }}
          onClose={() => setShowProcModal(false)}
          onDocumented={() => {
            setShowProcModal(false);
            // Reload executions
            api.get(`/procedures/executions/${visitId}`).then(r => {
              setExecutions(r.data.executions || []);
            }).catch(() => {});
          }}
        />
      )}
    </div>
  );
}
